// Admin → Systems: every cron job and long-running service with when it last
// ran (server pkg/systemruns ledger, written by cmd/jobrun and the services).

import FetchClient, { type ApiResponse } from "./FetchClient";

export type SystemHealth = "ok" | "running" | "late" | "failed" | "partial" | "unknown" | "off";

export interface SystemHostState {
  job: string;
  host: string;
  status: string;
  running: boolean;
  runningSince?: number;
  lastStartedAt?: number;
  lastFinishedAt?: number;
  lastSuccessAt?: number;
  lastFailureAt?: number;
  lastIdleAt?: number;
  lastSeenAt?: number;
  lastDurationMs?: number;
  lastExitCode: number;
  failStreak?: number;
  note?: string;
  lastError?: string[];
  health: SystemHealth;
}

export interface SystemRow {
  id: string;
  name: string;
  group: string;
  where: string;
  schedule: string;
  description?: string;
  kind: "cron" | "service" | "external";
  fleet?: boolean;
  off?: string;
  staleAfterSec?: number;
  health: SystemHealth;
  reason?: string;
  lastRunAt?: number;
  lastSuccessAt?: number;
  lastFailureAt?: number;
  lastSeenAt?: number;
  lastDurationMs?: number;
  lastExitCode: number;
  running?: number;
  runningSince?: number;
  hostsOk?: number;
  hostsTotal?: number;
  lastError?: string[];
  note?: string;
  signalSource?: string;
  hosts?: SystemHostState[];
  hours?: string[];
}

export interface SystemsData {
  generatedAt: number;
  groups: string[];
  systems: SystemRow[];
}

export interface SystemRun {
  job: string;
  host: string;
  status: string;
  startedAt: number;
  finishedAt?: number;
  durationMs?: number;
  exitCode: number;
  note?: string;
  tail?: string[];
}

export async function getSystems(): Promise<SystemsData> {
  const response = await FetchClient.get<ApiResponse<SystemsData>>("/admin/systems");
  if (!response?.data) throw new Error("Systems endpoint returned no data");
  return response.data;
}

export async function getSystemHistory(job: string, limit = 50): Promise<SystemRun[]> {
  const response = await FetchClient.get<ApiResponse<{ job: string; runs: SystemRun[] }>>(
    `/admin/systems/history?job=${encodeURIComponent(job)}&limit=${limit}`,
  );
  return response?.data?.runs ?? [];
}

export async function forgetSystemHost(job: string, host: string): Promise<void> {
  await FetchClient.delete(`/admin/systems/host?job=${encodeURIComponent(job)}&host=${encodeURIComponent(host)}`);
}
